import { useSyncExternalStore } from "react";
import { api, apiUrl } from "@/lib/api";
import { locale, t } from "@/lib/i18n";
import { goAnchor } from "./app-nav";

/* Общее состояние журнала замечаний (данные с бэкенда /api/feedback): плашка обратной связи добавляет записи,
   журнал в настройках читает их, меняет статус и ставит в очередь робота. Статусы — как в zakup.starec.ai.
   Хранилище вне React, чтобы оба окна видели одни и те же данные. */

export type FeedbackKind = "problem" | "idea";
export type FeedbackStatus = "new" | "work" | "testing" | "done" | "reject";

export type FeedbackEvent = { id: number; type: string; from_value?: string | null; to_value?: string | null; text?: string | null; created_at: string; author?: string | null };

export type FeedbackItem = {
  id: number;
  kind: FeedbackKind;
  status: FeedbackStatus;
  priority: "low" | "normal" | "high" | "critical";
  message: string;
  screen?: string | null;
  anchor?: string | null;
  note?: string | null;
  context?: { viewport?: string } | null;
  created_at: string;
  user_id: string;
  user_name?: string | null;
  user_login?: string | null;
  has_screenshot: boolean;
  fix_state?: "queued" | "running" | "failed" | null;
  fix_note?: string | null;
  comments: number;
  events?: FeedbackEvent[];
};

export const feedbackKindMeta: Record<FeedbackKind, { label: string; icon: string; hint: string }> = {
  idea: { label: t("Идея"), icon: "ri-lightbulb-line", hint: t("Чего не хватает в продукте") },
  problem: { label: t("Проблема"), icon: "ri-bug-line", hint: t("Что работает неправильно") },
};

export const feedbackStatusMeta: Record<FeedbackStatus, { label: string; icon: string }> = {
  new: { label: t("Новое"), icon: "ri-inbox-line" },
  work: { label: t("В работе"), icon: "ri-loader-4-line" },
  testing: { label: t("Исправлено — проверьте"), icon: "ri-checkbox-circle-line" },
  done: { label: t("Принято"), icon: "ri-check-double-line" },
  reject: { label: t("Отклонено"), icon: "ri-close-circle-line" },
};

export const screenshotUrl = (id: number) => apiUrl(`/feedback/${id}/screenshot`);

export const titleOf = (text: string) => {
  const line = text.trim().split(/\n+/)[0] || "";
  return line.length > 72 ? `${line.slice(0, 71)}…` : line;
};

export const when = (iso: string) =>
  new Date(iso).toLocaleString(locale, { day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" }).replace(" в ", ", ").replace(" at ", ", ");

let items: FeedbackItem[] = [];
let loaded = false;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

export const subscribeFeedback = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const getFeedback = () => items;

export async function reloadFeedback() {
  items = await api<FeedbackItem[]>("/feedback");
  loaded = true;
  emit();
}

const replace = (it: FeedbackItem) => {
  items = items.some((x) => x.id === it.id) ? items.map((x) => (x.id === it.id ? it : x)) : [it, ...items];
  emit();
  return it;
};

export type NewFeedback = { kind: FeedbackKind; text: string; screen: string; anchor: string; viewport: string; screenshot?: string };

const dataUrlToBlob = async (dataUrl: string) => (await fetch(dataUrl)).blob();

export async function addFeedback(input: NewFeedback): Promise<FeedbackItem> {
  const fd = new FormData();
  fd.set("kind", input.kind);
  fd.set("message", input.text.trim());
  fd.set("screen", input.screen);
  fd.set("anchor", input.anchor);
  fd.set("page", window.location.pathname + window.location.hash);
  fd.set("context", JSON.stringify({ viewport: input.viewport, url: window.location.href }));
  if (input.screenshot) fd.set("screenshot", await dataUrlToBlob(input.screenshot), "screen.jpg");
  return replace(await api<FeedbackItem>("/feedback", { method: "POST", body: fd }));
}

export type FeedbackPatch = { status?: FeedbackStatus; priority?: FeedbackItem["priority"]; note?: string; comment?: string };

export const patchFeedback = async (id: number, body: FeedbackPatch) => replace(await api<FeedbackItem>(`/feedback/${id}`, { method: "PATCH", json: body }));
export const queueFix = async (id: number) => replace(await api<FeedbackItem>(`/feedback/${id}/fix`, { method: "POST" }));
export const commentFeedback = async (id: number, text: string) => replace(await api<FeedbackItem>(`/feedback/${id}/comments`, { method: "POST", json: { text } }));
export const loadFeedbackItem = async (id: number) => replace(await api<FeedbackItem>(`/feedback/${id}`));

export async function deleteFeedback(id: number) {
  await api(`/feedback/${id}`, { method: "DELETE" });
  items = items.filter((x) => x.id !== id);
  emit();
}

/** Попросить окно настроек открыть журнал замечаний (плашка и уведомления живут вне окна настроек) */
export const FEEDBACK_JOURNAL_EVENT = "hub:feedback-journal";

// запрос «открыть журнал (и замечание №id)» ждёт, пока окно настроек появится на экране: в рабочем
// пространстве открыт только один раздел, и слушателя события в этот момент может не быть
let journalRequest: { id: number | null } | null = null;

export function requestFeedbackJournal(id?: number) {
  journalRequest = { id: id ?? null };
  window.dispatchEvent(new CustomEvent(FEEDBACK_JOURNAL_EVENT));
  goAnchor("#settings");
}

/** Есть ли невыполненный запрос открыть журнал (окно настроек смотрит при появлении) */
export const journalRequested = () => journalRequest !== null;

/** Забрать запрос: номер замечания, которое нужно раскрыть, или null */
export function takeJournalRequest() {
  const req = journalRequest;
  journalRequest = null;
  return req;
}

export default function useFeedback() {
  const list = useSyncExternalStore(subscribeFeedback, getFeedback, getFeedback);
  if (!loaded) {
    loaded = true;
    reloadFeedback().catch(() => {
      loaded = false;
    });
  }
  const count = (status: FeedbackStatus) => list.filter((i) => i.status === status).length;
  return {
    items: list,
    counts: { new: count("new"), work: count("work"), testing: count("testing"), done: count("done"), reject: count("reject"), total: list.length },
    reload: reloadFeedback,
  };
}
